import { ArrowRight, BookMarked, CheckCircle2, FlaskConical, LockKeyhole } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeading } from "../components/PageHeading";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { api } from "../lib/api";

export function LearnPage() {
  const { track, practice } = usePortal();
  const capabilities = useCapabilities();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  if (!track) return <div className="loading-state">正在读取训练内容</div>;

  async function startDeck(deck: string) {
    setError(null);
    try {
      const session = await api.startSession(track!.slug, { deck });
      navigate(`/learn/session/${session.id}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }

  return (
    <>
      <PageHeading number="02" title="开始学习" description="先读全景教材，再逐个学习模块；每个模块先讲知识，之后才用选择题和小案例纠偏。" />
      {error && <div className="error-banner" role="alert">{error}</div>}
      {Boolean(practice?.active_sessions.length) && <section className="resume-band">
        <strong>继续未完成训练</strong>
        <div>{practice!.active_sessions.map((session) => <Link key={session.id} to={`/learn/session/${session.id}`}><span>{session.qualifying ? "正式测试" : "形成性练习"}</span><b>{session.title}</b><small>{session.answered} / {session.total}</small><ArrowRight size={17} /></Link>)}</div>
      </section>}
      <section className="section-block">
        <div className="section-title-row"><div><span className="section-number">02.1</span><h2>教材学习路径</h2></div><BookMarked size={23} /></div>
        <div className="lesson-grid">
          {track.published_artifacts.filter((artifact) => ["textbook", "module-textbook"].includes(artifact.kind)).map((artifact, index) => (
            <Link className="lesson-row" to={`/resources/textbook/${artifact.token}`} key={artifact.token}>
              <span className="lesson-number">{String(index).padStart(2, "0")}</span>
              <div><strong>{artifact.label}</strong><span>{artifact.kind === "textbook" ? "先看完整知识体系与学习地图" : "详细概念、逻辑、案例与相关知识"}</span></div>
              <ArrowRight size={19} />
            </Link>
          ))}
        </div>
      </section>

      <section className="section-block">
        <div className="section-title-row"><div><span className="section-number">02.2</span><h2>进阶项目训练</h2></div><BookMarked size={23} /></div>
        <div className="lesson-grid">
          {track.lesson_catalog.map((lesson) => {
            const completed = practice?.lesson_progress[lesson.id]?.completed;
            return (
              <Link className="lesson-row" to={`/learn/reader/${lesson.id}`} key={lesson.id}>
                <span className="lesson-number">{String(lesson.number).padStart(2, "0")}</span>
                <div><strong>{lesson.title}</strong><span>{lesson.section_count} 节 · 约 {lesson.estimated_minutes} 分钟</span></div>
                {completed ? <CheckCircle2 size={19} /> : <ArrowRight size={19} />}
              </Link>
            );
          })}
        </div>
      </section>

      <section className="section-block">
        <div className="section-title-row"><div><span className="section-number">02.3</span><h2>训练题库</h2></div><FlaskConical size={23} /></div>
        <div className="deck-grid">
          {track.practice_catalog.map((deck) => {
            const progress = practice?.deck_progress[deck.id];
            const disabledByMode = !capabilities.practice.writable
              || (deck.qualifying && !capabilities.practice.formal_assessment);
            const unavailable = disabledByMode || (deck.qualifying && progress?.startable === false);
            return (
              <article className="deck-item" key={deck.id}>
                <div className="deck-meta"><span>{deck.qualifying ? "正式证据" : "形成性练习"}</span><b>{deck.item_count} 题</b></div>
                <h3>{deck.title}</h3>
                <p>{deck.description}</p>
                <div className="deck-progress"><span>已作答 {progress?.attempted || 0}</span><span>{deck.qualifying ? "未见可用" : "可练习"} {progress?.available ?? deck.item_count}</span></div>
                <button className="command-button" type="button" onClick={() => void startDeck(deck.id)} disabled={unavailable}>
                  {unavailable ? <><LockKeyhole size={17} />{disabledByMode ? "当前模式不可写" : "未见题不足"}</> : <>开始练习<ArrowRight size={17} /></>}
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}
